// Email HTML to plain text for the AI readers (the inquiry wizard, supplier rate replies, pasted
// rates), keeping the structure the AI needs: tables become one row per line with " | " between
// cells, paragraphs, line breaks and list items keep their lines. Taking an email's textContent
// glued table cells together ("40HCUSD 1850Shanghai"), and sending the raw HTML buried the table in
// styles and Outlook markup. Plain string work, so it runs in the browser and on the server.

const ENTITIES = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'", ndash: '–', mdash: '—', hellip: '…', euro: '€', pound: '£', deg: '°', rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“', bull: '•', middot: '·', times: '×', rarr: '→', larr: '←', harr: '↔' };

function decodeEntities(s) {
    return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+\d*);/gi, (all, name) => {
        if (name[0] === '#') {
            const code = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
            return Number.isFinite(code) ? String.fromCodePoint(code) : all;
        }
        return ENTITIES[name.toLowerCase()] ?? all;
    });
}

const BLOCK = 'p|div|h[1-6]|ul|ol|table|thead|tbody|tfoot|blockquote|section|article|header|footer|pre|address|center|form|fieldset|hr';

// Text of a fragment with no tables left in it: line breaks for blocks, "- " for list items.
function blockText(html) {
    return html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<li\b[^>]*>/gi, '\n- ')
        .replace(new RegExp(`</?(?:${BLOCK})\\b[^>]*>`, 'gi'), '\n')
        .replace(/<[^>]+>/g, '');
}

function cleanLines(text) {
    return text
        .split('\n')
        .map(line => line.replace(/[ \t ]+/g, ' ').trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

// One table with no table inside it. A data table (some row has two or more filled cells) becomes
// "a | b | c" lines; a layout table (one cell per row, or cells holding several lines) keeps each
// cell's text on its own lines.
function tableText(inner) {
    const rows = [];
    inner.replace(/<tr\b[^>]*>([\s\S]*?)(?=<tr\b|<\/table>|$)/gi, (m, row) => {
        const cells = [];
        row.replace(/<t[dh]\b[^>]*>([\s\S]*?)(?=<t[dh]\b|<\/tr>|$)/gi, (m2, cell) => {
            cells.push(cleanLines(decodeEntities(blockText(cell.replace(/<\/t[dh]>/gi, '')))));
            return '';
        });
        if (cells.length) rows.push(cells);
        return '';
    });
    if (!rows.length) return cleanLines(decodeEntities(blockText(inner)));
    const layout = rows.some(cells => cells.some(c => c.includes('\n'))) || !rows.some(cells => cells.filter(Boolean).length > 1);
    if (layout) return rows.map(cells => cells.filter(Boolean).join('\n')).filter(Boolean).join('\n');
    // Drop empty columns at the ends; keep empty cells between, so columns stay aligned.
    return rows.map(cells => {
        let a = 0;
        let b = cells.length;
        while (a < b && !cells[a]) a++;
        while (b > a && !cells[b - 1]) b--;
        return cells.slice(a, b).join(' | ');
    }).filter(Boolean).join('\n');
}

export function emailHtmlToText(html) {
    if (!html) return '';
    let s = String(html)
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<(head|style|script|title|xml)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
        .replace(/<\/?o:p>/gi, '');
    // Innermost tables first, so a data table inside a layout table keeps its rows.
    const innermost = /<table\b[^>]*>((?:(?!<table\b)[\s\S])*?)<\/table>/i;
    let guard = 0;
    while (innermost.test(s) && guard++ < 500) {
        // Placeholder-free: the converted text has no tags, so outer passes leave it alone; line
        // breaks are encoded as <br> so an outer cell sees them as lines, not spaces.
        s = s.replace(innermost, (m, inner) => `<div>${tableText(inner).split('\n').map(l => l.replace(/[<>]/g, c => (c === '<' ? '&lt;' : '&gt;'))).join('<br>')}</div>`);
    }
    return cleanLines(decodeEntities(blockText(s)));
}

// Whether a string is HTML (an email body) rather than plain text.
export function looksLikeHtml(text) {
    return /<(html|body|div|p|br|table|span|td)\b[^>]*>/i.test(String(text || ''));
}

// For a paste into a text box: the clipboard's HTML as text when it holds a table (which plain
// text loses or flattens), otherwise null so the normal paste goes ahead.
export function pastedTableText(clipboardData) {
    const html = clipboardData?.getData?.('text/html');
    if (!html || !/<table\b/i.test(html)) return null;
    const text = emailHtmlToText(html);
    return text || null;
}

// onPaste for a text box whose value is held in state: a paste holding a table goes in as rows
// with " | " between cells, at the cursor; any other paste is left to the browser.
export function pasteKeepingTables(event, setValue) {
    const text = pastedTableText(event.clipboardData);
    if (!text) return;
    event.preventDefault();
    const el = event.target;
    const value = el.value || '';
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    setValue(value.slice(0, start) + text + value.slice(end));
}
